import React, { useMemo, useState } from "react";
import { Box, IconButton, Typography, alpha } from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { COLORS } from "../../styles/uiConstants";
import { useClinicTheme } from "../../theme";

export const MONTH_NAMES = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

const DAY_HEADERS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

export const DAY_INDEX_TO_TR = {
  1: "Pazartesi", 2: "Salı", 3: "Çarşamba",
  4: "Perşembe", 5: "Cuma", 6: "Cumartesi", 0: "Pazar",
};

const pad2 = (n) => String(n).padStart(2, "0");

/** "YYYY-MM-DD" → yerel gece yarısı Date; boş/geçersizse null. */
export const parseYMD = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(`${v}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Date → "YYYY-MM-DD" (UTC kaymasız, yerel alanlardan kurulur). */
export const toYMD = (d) => {
  if (!d || Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/** "YYYY-MM-DD" → "01 Ocak 2026 Perşembe" */
export const formatDateTR = (v) => {
  const d = parseYMD(v);
  if (!d) return "";
  return d.toLocaleDateString("tr-TR", {
    day: "2-digit", month: "long", year: "numeric", weekday: "long",
  });
};

/** "YYYY-MM-DD" → "01.01.2026" */
export const formatDateShortTR = (v) => {
  const d = parseYMD(v);
  if (!d) return "";
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
};

/**
 * Takvim yüzeyi — ay navigasyonu + gün ızgarası + (opsiyonel) çalışma günü
 * lejandı. Uygulamadaki TÜM tarih seçicileri bu yüzeyi kullanır; tetikleyici
 * (kart / input) çağırana aittir.
 *
 * @param {string}   value            Seçili tarih "YYYY-MM-DD"
 * @param {Function} onChange         (dateStr) => void
 * @param {string}   [minDate]        Bu tarihten öncesi seçilemez "YYYY-MM-DD"
 * @param {string}   [maxDate]        Bu tarihten sonrası seçilemez "YYYY-MM-DD"
 * @param {Array}    [workingSchedule] [{ day, startTime, endTime }] — verilirse
 *   açık/kapalı günler noktalarla işaretlenir ve altta lejant çıkar.
 */
export default function CalendarSurface({
  value,
  onChange,
  minDate,
  maxDate,
  workingSchedule = [],
}) {
  const { palette } = useClinicTheme();
  const primary = palette.primary;

  const selected = useMemo(() => parseYMD(value), [value]);
  // Yerel gece yarısı — her render'da yeni Date üretmemek için memoize edilir,
  // yoksa aşağıdaki useMemo bağımlılığı sürekli değişir.
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const initial = selected || today;
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());

  const minD = useMemo(() => parseYMD(minDate), [minDate]);
  const maxD = useMemo(() => parseYMD(maxDate), [maxDate]);

  const hasSchedule = workingSchedule.length > 0;
  const workingDays = useMemo(() => {
    const set = new Set();
    workingSchedule.forEach((s) => set.add(s.day));
    return set;
  }, [workingSchedule]);

  const calendarDays = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

    // Pazartesi başlangıçlı ızgara.
    let startDow = firstDay.getDay();
    startDow = startDow === 0 ? 6 : startDow - 1;

    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(viewYear, viewMonth, d);
      const dayName = DAY_INDEX_TO_TR[date.getDay()];
      const entry = workingSchedule.find((s) => s.day === dayName);
      const outOfRange = (minD && date < minD) || (maxD && date > maxD);

      cells.push({
        day: d,
        dateStr: toYMD(date),
        dayName,
        isWorkingDay: hasSchedule ? workingDays.has(dayName) : true,
        isToday: date.getTime() === today.getTime(),
        isSelected: Boolean(selected) && date.getTime() === selected.getTime(),
        disabled: Boolean(outOfRange),
        scheduleInfo: entry ? `${entry.startTime} – ${entry.endTime}` : null,
      });
    }

    return cells;
  }, [viewYear, viewMonth, workingDays, hasSchedule, workingSchedule, minD, maxD, selected, today]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear((y) => y - 1); setViewMonth(11); }
    else setViewMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear((y) => y + 1); setViewMonth(0); }
    else setViewMonth((m) => m + 1);
  };

  return (
    <Box>
      {/* Ay navigasyonu */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        px: 1.5, py: 0.8,
        bgcolor: COLORS.slate50,
        borderBottom: `1px solid ${COLORS.slate200}`,
      }}>
        <IconButton size="small" onClick={prevMonth} aria-label="Önceki ay">
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontWeight: 800, fontSize: 14, color: COLORS.slate800 }}>
          {MONTH_NAMES[viewMonth]} {viewYear}
        </Typography>
        <IconButton size="small" onClick={nextMonth} aria-label="Sonraki ay">
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Gün başlıkları */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", px: 0.8, pt: 0.8 }}>
        {DAY_HEADERS.map((d, i) => (
          <Box key={d} sx={{
            textAlign: "center", py: 0.3, fontSize: 11, fontWeight: 700,
            color: i >= 5 ? COLORS.slate400 : COLORS.slate500,
          }}>
            {d}
          </Box>
        ))}
      </Box>

      {/* Gün ızgarası */}
      <Box sx={{
        display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px",
        px: 0.8, pb: 1, pt: 0.3,
      }}>
        {calendarDays.map((cell, idx) => {
          if (!cell) return <Box key={`e-${idx}`} />;

          const { day, isWorkingDay, isToday, isSelected, disabled } = cell;

          let bgColor = "transparent";
          let textColor = COLORS.slate800;
          let border = "2px solid transparent";
          let hoverBg = COLORS.slate100;
          let fontWeight = 500;
          let opacity = 1;
          let dotColor = null;

          if (isSelected) {
            bgColor = primary;
            textColor = COLORS.white;
            fontWeight = 800;
            hoverBg = primary;
            border = `2px solid ${primary}`;
          } else if (disabled) {
            textColor = COLORS.slate300;
            opacity = 0.5;
            hoverBg = "transparent";
          } else if (hasSchedule && !isWorkingDay) {
            textColor = COLORS.slate400;
            bgColor = COLORS.slate50;
            dotColor = COLORS.error;
          } else if (hasSchedule) {
            dotColor = COLORS.success;
          }

          if (isToday && !isSelected) {
            border = `2px solid ${alpha(primary, 0.5)}`;
            fontWeight = 800;
          }

          return (
            <Box
              key={cell.dateStr}
              onClick={() => !disabled && onChange?.(cell.dateStr)}
              title={
                hasSchedule
                  ? `${cell.dayName}: ${isWorkingDay ? cell.scheduleInfo || "Açık" : "Kapalı"}`
                  : ""
              }
              sx={{
                position: "relative",
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                height: 34, borderRadius: 1.5,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity, bgcolor: bgColor, border,
                transition: "all 0.12s ease",
                "&:hover": { bgcolor: disabled ? bgColor : hoverBg },
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight, color: textColor, lineHeight: 1 }}>
                {day}
              </Typography>
              {dotColor && !isSelected && (
                <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: dotColor, mt: 0.2 }} />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Lejant — yalnızca klinik çalışma takvimi verildiğinde */}
      {hasSchedule && (
        <Box sx={{
          display: "flex", gap: 2, px: 1.5, py: 0.8,
          borderTop: `1px solid ${COLORS.slate200}`, bgcolor: COLORS.slate50,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: COLORS.success }} />
            <Typography sx={{ fontSize: 10, color: COLORS.slate500 }}>Açık</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: COLORS.error }} />
            <Typography sx={{ fontSize: 10, color: COLORS.slate500 }}>Kapalı</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
